import { Alert, Box, Button, Typography } from "@mui/material";
import { useGetIdentity, type UserIdentity } from "react-admin";
import { useActivePartner } from "@/hooks/useActivePartner";
import { openPartnerSwitcher } from "@/components/partner/partnerSwitcherStore";

interface AssignedRole {
  role: { name: string };
}

// assignedRoles is a backend extension exposed via UserIdentity's index signature.
const hasSuperAdminRole = (identity: UserIdentity | undefined): boolean => {
  const assignedRoles = identity?.assignedRoles as AssignedRole[] | undefined;
  return assignedRoles?.some((ar) => ar.role.name === "SuperAdmin") ?? false;
};

export const SuperAdminContextBanner = () => {
  const { data: identity, isLoading: identityLoading } = useGetIdentity();
  const superAdmin = hasSuperAdminRole(identity);
  const { partnerName, readOnly, isLoading } = useActivePartner(superAdmin);

  // Wait until readOnly is resolved so we never flash the writable state before flipping.
  if (identityLoading || !superAdmin || isLoading || readOnly === undefined) return null;

  const name = partnerName ?? "Unknown partner";

  const stickyTop = {
    position: "sticky" as const,
    top: 0,
    zIndex: (theme: { zIndex: { appBar: number } }) => theme.zIndex.appBar + 1,
    width: "100%",
  };

  if (readOnly) {
    return (
      <Alert severity="error" variant="filled" square sx={{ ...stickyTop, borderRadius: 0, alignItems: "center" }}>
        <Box sx={{ display: "flex", alignItems: "center", gap: 1, flexWrap: "wrap" }}>
          <span>
            Read-only — you can&apos;t make changes while acting as <strong>{name}</strong>.
          </span>
          <Button
            color="inherit"
            size="small"
            variant="outlined"
            onClick={openPartnerSwitcher}
            sx={{ py: 0, px: 1, borderColor: "rgba(255,255,255,0.7)" }}
          >
            Switch partner
          </Button>
        </Box>
      </Alert>
    );
  }

  return (
    <Box
      sx={{
        ...stickyTop,
        display: "flex",
        alignItems: "center",
        gap: 1,
        bgcolor: "action.hover",
        borderBottom: "1px solid",
        borderColor: "divider",
        px: 2,
        py: 0.5,
      }}
    >
      <Typography variant="caption" sx={{ color: "text.secondary" }}>
        Acting as <strong>{name}</strong>
      </Typography>
      <Button
        size="small"
        variant="text"
        onClick={openPartnerSwitcher}
        sx={{ py: 0, px: 0.5, minWidth: 0, fontSize: "0.75rem" }}
      >
        Switch partner
      </Button>
    </Box>
  );
};
